import { type PropsWithChildren, useMemo } from 'react';
import { type MarketingPreferencesResponse } from '@cx-sdk/myoc';
import { type SiteConfig } from '@cx-web/api-clients/site-config-api/types';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import Betslip from '@cx-web/feature-betslip/components/betslip';

import CookieBanner from '~/components/composites/CookieBanner';
import { LegacyAuthModal } from '~/components/composites/legacy-auth-modal';
import { GeolocationProvider } from '~/providers/geolocation-context';
import { LayoutStoreProvider } from '~/providers/layout-store-provider';
import { MyOCAuthProvider } from '~/providers/myoc-auth';
import { NavigationProvider } from '~/providers/navigation-context';
import { UIConfigProvider } from '~/providers/ui-config-context';
import { type loader } from '~/routes/$/loader.server';
import { type SerializeFrom } from '~/types/serialize-from';

import { LayoutComponents } from './components/LayoutComponents';

export type StandardLayoutLoaderData = {
  footerData?: any;
  navData: any;
  siteConfig: SiteConfig;
};

export function StandardLayout({
  adUnitConfiguration,
  baseUrlWithProtocolAndRepub,
  children,
  currentPath,
  deviceType,
  footerData,
  geolocationInfo,
  isAdmiralCookieBannerEnabled,
  marketingPreferences,
  myocConfig,
  myocData,
  navData,
  oddsFormat,
  repub,
  resourcesUrl,
  showCookieBanner,
}: PropsWithChildren<
  SerializeFrom<typeof loader> &
    StandardLayoutLoaderData & {
      marketingPreferences?: MarketingPreferencesResponse;
      showCookieBanner?: boolean;
    }
>) {
  const renderBetslip = () => {
    if (currentPath !== 'betslip') {
      return <Betslip />;
    }

    return null;
  };

  const renderCookieBanner = () => {
    const permittedRepubs = ['ES', 'IT'];
    if (!permittedRepubs.includes(repub.toUpperCase())) {
      return null;
    }
    return <CookieBanner repub={repub as 'ES' | 'IT'} showBanner={showCookieBanner} />;
  };

  const layoutStoreValue = useMemo(
    () => ({
      deviceType,
      footerData,
      isAdmiralCookieBannerEnabled,
      navData,
      repub,
      resourcesUrl,
    }),
    [deviceType, footerData, isAdmiralCookieBannerEnabled, navData, repub, resourcesUrl],
  );

  return (
    <GeolocationProvider state={geolocationInfo.subdivisionCode}>
      <NavigationProvider currentPath={currentPath}>
        <UIConfigProvider deviceType={deviceType}>
          <MyOCAuthProvider
            marketingPreferences={marketingPreferences}
            myocConfig={myocConfig}
            myocData={myocData as MyOCData}
          >
            <LayoutStoreProvider value={layoutStoreValue}>
              <LayoutComponents adUnitConfiguration={adUnitConfiguration} userOddsFormat={oddsFormat}>
                {children}
              </LayoutComponents>
            </LayoutStoreProvider>
            {renderBetslip()}
            <LegacyAuthModal baseUrl={baseUrlWithProtocolAndRepub} />
            {renderCookieBanner()}
          </MyOCAuthProvider>
        </UIConfigProvider>
      </NavigationProvider>
    </GeolocationProvider>
  );
}
